import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, Clock, ArrowUpRight, Sparkles } from "lucide-react";

const ADDRESS_LINES = ["Batra Electronics", "55 Dispensary Road", "Dehradun, Uttarakhand 248001"];
const PHONE = "+91 80189 11999";
const PHONE_HREF = "tel:+918018911999";
const EMAIL = "batraelectronics55@gmail.com";
const WHATSAPP_HREF = "https://wa.me/918018911999";
const MAP_QUERY = encodeURIComponent("Batra Electronics, 55 Dispensary Road, Dehradun, Uttarakhand 248001");
const MAP_EMBED = `https://www.google.com/maps?q=${MAP_QUERY}&output=embed`;
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${MAP_QUERY}`;

export default function Contact() {
  const [f, setF] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", { ...f, kind: "contact" });
      toast.success("Message received. Our team will respond within 24 hours.");
      setF({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try calling us directly.");
    } finally {
      setLoading(false);
    }
  };

  const items = [
    {
      i: MapPin,
      t: "Showroom",
      lines: ADDRESS_LINES,
      href: MAP_LINK,
      cta: "Open in Maps",
      testid: "contact-item-address",
    },
    {
      i: Phone,
      t: "Phone",
      lines: [PHONE],
      href: PHONE_HREF,
      cta: "Call now",
      testid: "contact-item-phone",
    },
    {
      i: MessageCircle,
      t: "WhatsApp",
      lines: ["Chat with us — response within minutes"],
      href: WHATSAPP_HREF,
      cta: "Open WhatsApp",
      testid: "contact-item-whatsapp",
    },
    {
      i: Mail,
      t: "Email",
      lines: [EMAIL],
      href: `mailto:${EMAIL}`,
      cta: "Send email",
      testid: "contact-item-email",
    },
    {
      i: Clock,
      t: "Hours",
      lines: ["Mon–Sat · 10:30 to 20:00", "By appointment"],
      testid: "contact-item-hours",
    },
  ];

  return (
    <div className="pt-32 pb-24 container-x" data-testid="contact-page">
      <div className="max-w-3xl mb-14">
        <div className="kicker mb-3">Contact</div>
        <h1 className="font-display text-5xl md:text-7xl text-white">
          Speak with our <em className="not-italic gold-text">atelier.</em>
        </h1>
        <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
          Visit our Dehradun showroom, or start a conversation online. We reply personally within one business day.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Form */}
        <form
          onSubmit={submit}
          className="glass rounded-3xl p-8 md:p-10 space-y-4 bg-charcoal-900/60 border border-white/10"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="c-name" className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Name</label>
              <input id="c-name" type="text" required placeholder="Your full name"
                value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                className="w-full" data-testid="contact-name" />
            </div>
            <div>
              <label htmlFor="c-email" className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Email</label>
              <input id="c-email" type="email" required placeholder="you@example.com"
                value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                className="w-full" data-testid="contact-email" />
            </div>
          </div>
          <div>
            <label htmlFor="c-phone" className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Phone</label>
            <input id="c-phone" type="tel" placeholder="+91 —"
              value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
              className="w-full" data-testid="contact-phone" />
          </div>
          <div>
            <label htmlFor="c-subject" className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Subject</label>
            <input id="c-subject" type="text" placeholder="Home theatre consultation, service…"
              value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })}
              className="w-full" data-testid="contact-subject" />
          </div>
          <div>
            <label htmlFor="c-message" className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Message</label>
            <textarea id="c-message" rows="6" required placeholder="Tell us about your project…"
              value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })}
              className="w-full" data-testid="contact-message"></textarea>
          </div>
          <button type="submit" disabled={loading} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="contact-submit">
            {loading ? "Sending…" : "Send Message"}
          </button>
        </form>

        {/* Info + Map */}
        <div className="space-y-4">
          {items.map(({ i: I, t, lines, href, cta, testid }) => {
            const inner = (
              <>
                <div className="w-11 h-11 rounded-xl bg-gold/10 flex items-center justify-center text-gold shrink-0">
                  <I className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="kicker mb-1">{t}</div>
                  <div className="text-white/90 leading-snug">
                    {lines.map((l, idx) => (
                      <div key={idx} className={idx === 0 ? "text-white" : "text-white/70 text-sm"}>{l}</div>
                    ))}
                  </div>
                  {href && cta && (
                    <div className="mt-2 text-xs text-gold inline-flex items-center gap-1">
                      {cta} <ArrowUpRight className="w-3 h-3" />
                    </div>
                  )}
                </div>
              </>
            );
            const base = "glass rounded-2xl p-5 flex gap-4 items-start bg-charcoal-900/60 border border-white/10 transition";
            return href ? (
              <a key={t} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                className={`${base} hover:border-gold/40`} data-testid={testid}>
                {inner}
              </a>
            ) : (
              <div key={t} className={base} data-testid={testid}>{inner}</div>
            );
          })}

          <div className="rounded-2xl overflow-hidden border border-white/10 bg-charcoal-900/60" data-testid="contact-map">
            <iframe
              title="Batra Electronics — Dehradun Showroom"
              src={MAP_EMBED}
              className="w-full h-72 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
}
